import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, Pencil, Save } from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../context/AuthContext";
import EntityChatPanel from "../components/chat/EntityChatPanel";
import FromChatPill from "../components/chat/FromChatPill";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";
import { toast } from "../components/ui/sonner";
import TicketOverviewSection from "./service/sections/TicketOverviewSection";
import TicketMediaSection from "./service/sections/TicketMediaSection";
import TicketNotesSection from "./service/sections/TicketNotesSection";
import JobCardPickerModal from "../components/JobCardPickerModal";

// Phase 7 Sub-iter 7A — status → EntityShell tone mapping.
const STATUS_TONE = {
  open: "blue",
  in_progress: "amber",
  awaiting_customer: "violet",
  awaiting_parts: "violet",
  resolved_via_jc: "green",
  resolved_external: "green",
  resolved_virtually: "green",
  cancelled: "slate",
};
const labelFromKey = (k) => (k || "").replaceAll("_", " ");

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";
  const [t, setT] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerSites, setCustomerSites] = useState([]);
  // Iter 430.t1 (Dirk, 2026-02-28) — the edit form now exposes a
  // customer picker + a (re-filtering) site picker, so we need the
  // full tenant customer list once edit mode is turned on.
  const [allCustomers, setAllCustomers] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("overview");
  // Iter 418.bd (Dirk, 2026-02-28) — Job Card picker modal state.
  // Opens when the user picks "Resolved via Job Card" status.
  const [jcPickerOpen, setJcPickerOpen] = useState(false);
  const [jcPickerContext, setJcPickerContext] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const list = await api.get(`/tickets`);
    const found = list.data.find((x) => x.id === id);
    if (!found) return;
    setT(found);
    // Iter 418.ba (Dirk, 2026-02-28) — Edit-mode must expose EVERY
    // editable field the create form does. Seed the form with the
    // full set so nothing silently reverts on save.
    // Iter 430.t1 (Dirk, 2026-02-28) — Also seed customer_id + ticket_date
    // so staff can move the ticket to a different customer or correct a
    // mis-logged incident date retroactively.
    setEditForm({
      subject: found.subject || "",
      description: found.description || "",
      support_type: found.support_type || "",
      status: found.status || "logged",
      priority: found.priority || "normal",
      customer_id: found.customer_id || "",
      site_id: found.site_id || "",
      ticket_date: found.ticket_date || found.created_at || "",
      external_ref: found.external_ref || "",
      resolution_notes: found.resolution_notes || "",
      time_logged: found.time_logged || "",
      hidden_from_client: !!found.hidden_from_client,
      assigned_to_id: found.assigned_to_id || "",
      linked_job_card_id: found.linked_job_card_id || "",
    });
    if (found.customer_id) {
      const c = await api.get(`/customers/${found.customer_id}`);
      setCustomer(c.data);
      try {
        const sitesRes = await api.get(`/sites?customer_id=${found.customer_id}`);
        const items = Array.isArray(sitesRes.data)
          ? sitesRes.data
          : (sitesRes.data?.items || []);
        setCustomerSites(items);
      } catch {
        setCustomerSites([]);
      }
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load staff once so the assignee dropdown can render.
  useEffect(() => {
    if (!isManager) return;
    api.get("/users")
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setStaff(items.filter((u) => u.role !== "client" && u.is_active !== false));
      })
      .catch(() => setStaff([]));
  }, [isManager]);

  // Iter 430.t1 — load all customers (first page is plenty for the
  // picker typeahead) the first time edit mode is turned on.
  useEffect(() => {
    if (!editMode || !isManager || allCustomers.length) return;
    api.get("/customers?limit=1000")
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setAllCustomers(items);
      })
      .catch(() => setAllCustomers([]));
  }, [editMode, isManager, allCustomers.length]);

  // Iter 430.t1 — when the edit form's customer changes mid-edit,
  // refetch that customer's sites so the Site picker reflects the new
  // customer's locations (and clear a stale site_id selection).
  useEffect(() => {
    if (!editMode) return;
    const cid = editForm.customer_id;
    if (!cid) { setCustomerSites([]); return; }
    let alive = true;
    api.get(`/sites?customer_id=${cid}`)
      .then(({ data }) => {
        if (!alive) return;
        const items = Array.isArray(data) ? data : (data?.items || []);
        setCustomerSites(items);
      })
      .catch(() => { if (alive) setCustomerSites([]); });
    return () => { alive = false; };
  }, [editMode, editForm.customer_id]);

  const assignTicket = async (uid) => {
    if (!t) return;
    try {
      const { data } = await api.put(`/tickets/${id}`, { ...t, assigned_to_id: uid || null });
      setT(data);
      const who = staff.find((s) => s.id === uid);
      toast.success(uid
        ? `Assigned to ${who?.name || "team member"} · notification + task created`
        : "Unassigned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign ticket.");
    }
  };

  // Iter 364 (2026-02-27) — viewing the ticket clears its badge and
  // notifies the bell to re-poll immediately.
  useEffect(() => {
    if (!id) return;
    api.post("/notifications/mark-entity-read", {
      entity_type: "ticket",
      entity_id: id,
    })
      .then(() => {
        try { window.dispatchEvent(new CustomEvent("hv:notifications-mutated")); }
        catch { /* old browsers */ }
      })
      .catch(() => {});
  }, [id]);
  // Auto-refresh in read-only mode only — would clobber in-progress edits.
  useAutoRefresh(load, { intervalMs: 45_000, enabled: !editMode });

  const updateStatus = async (status) => {
    // Iter 400 — plant SLA guard. If this ticket was logged with
    // plant_down and we're moving it to a resolved state, ask the tech
    // whether the plant is running again BEFORE we hit the server. The
    // backend also enforces the same guard (409 plant_status_confirmation_required)
    // so any surface that reaches the API keeps SLA data honest.
    const RESOLVED = ["resolved", "resolved_via_jc", "resolved_external", "closed"];
    let plantBackUpFlag = null;
    if (
      RESOLVED.includes(status)
      && t?.plant_down_at_creation
      && !t?.plant_back_up_at
    ) {
      const ok = window.confirm(
        "This ticket was logged with the plant DOWN.\n\n"
        + "Is the plant running again now?\n\n"
        + "• OK  → mark plant back UP and resolve\n"
        + "• Cancel → keep ticket open (SLA still counting downtime)",
      );
      if (!ok) return;
      plantBackUpFlag = true;
    }
    // Iter 418.bd (Dirk, 2026-02-28) — Resolving via Job Card must
    // link to an existing JC. Instead of prompting for a code, we open
    // a picker (customer's job cards, newest first) so the user just
    // clicks the right one. Cancel keeps status unchanged.
    if (status === "resolved_via_jc" && !t.linked_job_card_id) {
      setJcPickerContext({ status, plantBackUpFlag });
      setJcPickerOpen(true);
      return;
    }
    try {
      await api.put(`/tickets/${id}`, { ...t, status, ...(plantBackUpFlag ? { plant_back_up: true } : {}) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save ticket status.");
      return;
    }
    load();
  };

  // Iter 418.bd — helper the JC picker modal calls once the user picks.
  const linkJobCardAndResolve = async (jc) => {
    if (!jc) return;
    try {
      const ctx = jcPickerContext || {};
      await api.put(`/tickets/${id}`, {
        ...t,
        status: ctx.status || "resolved_via_jc",
        linked_job_card_id: jc.id,
        ...(ctx.plantBackUpFlag ? { plant_back_up: true } : {}),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link job card.");
      return;
    } finally {
      setJcPickerOpen(false);
      setJcPickerContext(null);
    }
    toast.success(`Linked to JC${jc.short_id || jc.id.slice(0, 8)} · ticket resolved`);
    load();
  };

  const handleSave = async () => {
    try {
      // Iter 418.ba (Dirk, 2026-02-28) — normalise empty selects back
      // to null so we don't store "" where the schema expects an id or
      // a number.
      const payload = { ...t, ...editForm };
      if (payload.assigned_to_id === "") payload.assigned_to_id = null;
      if (payload.site_id === "") payload.site_id = null;
      if (payload.linked_job_card_id === "") payload.linked_job_card_id = null;
      if (payload.time_logged === "" || payload.time_logged === undefined) {
        payload.time_logged = null;
      }
      await api.put(`/tickets/${id}`, payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save ticket changes.");
      return;
    }
    setEditMode(false);
    load();
  };

  const handleCancel = () => {
    setEditMode(false);
    load();
  };

  const handleDelete = async () => {
    await api.delete(`/tickets/${id}`);
    navigate("/tickets");
  };

  const convert = async () => {
    const { data } = await api.post(`/tickets/${id}/convert`, {});
    navigate(`/jobcards/${data.id}`);
  };

  if (!t) return <div className="text-[#647087]">Loading…</div>;

  // Phase 7 — header action cluster lives in EntityShell's `actions` slot.
  const headerActions = (
    <>
      {t?.source_chat_thread_id && (
        <FromChatPill
          threadId={t.source_chat_thread_id}
          messageId={t.source_chat_message_id}
        />
      )}
      {isManager && !editMode && (
        <button
          onClick={() => setEditMode(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="ticket-edit-btn"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
      )}
      {isManager && editMode && (
        <>
          <button onClick={handleCancel} className="hv-btn hv-btn-ghost text-xs" data-testid="ticket-cancel-btn">
            Cancel
          </button>
          <button onClick={handleSave} className="hv-btn hv-btn-primary text-xs" data-testid="ticket-save-btn">
            <Save className="w-3 h-3" /> Save
          </button>
        </>
      )}
      {isManager && !confirmDelete && (
        <button
          onClick={() => setConfirmDelete(true)}
          className="hv-btn hv-btn-ghost text-rose-300 hover:text-rose-200 text-xs"
          data-testid="ticket-delete-btn"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      )}
      {isManager && confirmDelete && (
        <>
          <button
            onClick={() => setConfirmDelete(false)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="ticket-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400 text-xs"
            data-testid="ticket-delete-confirm"
          >
            <Trash2 className="w-3 h-3" /> Confirm delete
          </button>
        </>
      )}
    </>
  );

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "media", label: "Media", count: (t.attachments || []).length || undefined },
    { key: "notes", label: "Comments", count: (t.notes || []).length || undefined },
    { key: "chat", label: "Chat" },
    { key: "history", label: "Activity" },
  ];

  return (
    <EntityShell
      shortId={t.short_id ? `#${t.short_id}` : undefined}
      kindLabel="Ticket"
      title={t.subject || "Ticket"}
      status={t.status ? { label: labelFromKey(t.status), tone: STATUS_TONE[t.status] || "slate" } : undefined}
      customerLink={customer ? {
        label: `View customer · ${customer.name}`,
        to: `/customers/${customer.id}`,
      } : undefined}
      backTo="/tickets"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      footerMeta={{
        createdByName: t.created_by_name,
        // Iter 290u — backend doesn't emit created_at_display; format
        // from raw created_at so the audit footer shows the date.
        createdAtDisplay: t.created_at_display
          || (t.created_at ? new Date(t.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
        updatedByName: t.updated_by_name,
        updatedAtDisplay: t.updated_at_display
          || (t.updated_at ? new Date(t.updated_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
      }}
    >
      <div data-testid="ticket-detail">
        {tab === "overview" && (
          <TicketOverviewSection
            t={t}
            customer={customer}
            customerSites={customerSites}
            allCustomers={allCustomers}
            editMode={editMode}
            editForm={editForm}
            setEditForm={setEditForm}
            isManager={isManager}
            staff={staff}
            onUpdateStatus={updateStatus}
            onAssign={assignTicket}
            onConvert={convert}
            onLinksChanged={load}
          />
        )}
        {tab === "media" && (
          <TicketMediaSection
            t={t}
            user={user}
            isManager={isManager}
            onReload={load}
          />
        )}
        {tab === "notes" && (
          <TicketNotesSection t={t} onReload={load} />
        )}
        {tab === "chat" && t?.id && (
          <div className="hv-card p-2" data-testid="ticket-chat-tab">
            <EntityChatPanel kind="ticket" entityId={t.id} />
          </div>
        )}
        {tab === "history" && (
          <HistoryBlock entity={{ id: t.id, kind: "ticket" }} />
        )}
      </div>
      <JobCardPickerModal
        open={jcPickerOpen}
        customerId={t?.customer_id}
        onPick={linkJobCardAndResolve}
        onClose={() => { setJcPickerOpen(false); setJcPickerContext(null); }}
      />
    </EntityShell>
  );
}
